import React from 'react'
import Head from 'next/head'
import renderLDJSON from './LDJSON'
import { PageMetaData } from 'types/metadata'

type Props = {
  pageMetaData?: PageMetaData
  pageTitle?: string
  ogPageTitle?: string
}

const MetaData = ({ pageMetaData, pageTitle, ogPageTitle }: Props) => {
  const { description, og, twitter, product, preloadImages, robots, schema } = pageMetaData || {}
  const {
    product: productSchema,
    breadcrumb: breadcrumbSchema,
    webpage: webpageSchema,
    website: websiteSchema,
    organization: organizationSchema,
    itemList: itemListSchema,
  } = schema || {}

  return (
    <Head>
      {pageTitle && <title key='title'>{pageTitle}</title>}

      {description && <meta name='description' key='description' content={description} />}

      {typeof robots === 'boolean' && (
        <meta
          name='robots'
          key='robots'
          content={
            robots
              ? 'max-image-preview:large, noai, noimageai'
              : 'noindex, nofollow, max-image-preview:large, noai, noimageai'
          }
        />
      )}

      {og && (
        <>
          {ogPageTitle && <meta key='og-title' property='og:title' content={ogPageTitle} />}
          {og.description && (
            <meta key='og-description' property='og:description' content={og.description} />
          )}
          {og.image && <meta key='og-image' property='og:image' content={og.image} />}
          {og.type && <meta key='og-type' property='og:type' content={og.type} />}
          {og.locale && <meta key='og-locale' property='og:locale' content={og.locale} />}
          {og.availability && (
            <meta key='og-availability' property='og:availability' content={og.availability} />
          )}
        </>
      )}

      {twitter && (
        <>
          {twitter.card && <meta key='twitter-card' name='twitter:card' content={twitter.card} />}
          {twitter.title && (
            <meta key='twitter-title' name='twitter:title' content={twitter.title} />
          )}
          {twitter.description && (
            <meta
              key='twitter-description'
              name='twitter:description'
              content={twitter.description}
            />
          )}
          {twitter.image && (
            <meta key='twitter-image' name='twitter:image' content={twitter.image} />
          )}
          {twitter.site && <meta key='twitter-site' name='twitter:site' content={twitter.site} />}
        </>
      )}

      {product?.price && (
        <>
          {product.price.currency && (
            <meta
              key='product-price-currency'
              property='product:price:currency'
              content={product.price.currency}
            />
          )}
          {product.price.amount !== undefined && (
            <meta
              key='product-price-amount'
              property='product:price:amount'
              content={String(product.price.amount)}
            />
          )}
        </>
      )}

      {(preloadImages || []).map((image, index) => (
        <link
          key={`preload-image-${index}`}
          rel='preload'
          fetchPriority='high'
          as='image'
          href={image}
        />
      ))}

      {organizationSchema && renderLDJSON(organizationSchema)}
      {websiteSchema && renderLDJSON(websiteSchema)}
      {webpageSchema && renderLDJSON(webpageSchema)}
      {breadcrumbSchema && renderLDJSON(breadcrumbSchema)}
      {productSchema && renderLDJSON(productSchema)}
      {itemListSchema && renderLDJSON(itemListSchema)}
    </Head>
  )
}

export default MetaData
